// The credit-close audit trail as it renders on screen — who closed the credit,
// when, why, and the free-text note when one was written.
//
// The server renders every string (closed_by_label / closed_at_label /
// closed_reason_label via server/lib/creditClose.js) so the row menu, the mobile
// sheet and the Excel export can never describe the same credit differently —
// the payments audit trail learned that the hard way. The client decides
// STYLING only, keyed off closed_by_kind.
//
// ONE component for both surfaces on purpose. This used to be a desktop table
// column (a clipped cell with the address hidden behind a `title` tooltip) plus
// a separately hand-written set of rows in CreditDetailSheet, and the two had
// already started labelling the same facts differently. It now renders the same
// `.sheet-kv` rows wherever it is mounted: the desktop row's ⋮ → "הערות"
// popover, and the mobile detail sheet.
//
// Three "who" states, all meaningful and none of them "nobody":
//   'user'    — the address of whoever closed it.
//   'unknown' — 'לא תועד' ("not recorded"): closed before the columns existed
//               (Aug 2026). Muted and italic, because it is an admission rather
//               than a value — never "nobody did it", and never a guess.
//   'system'  — the '@system' sentinel. Nothing machine-closes a credit today;
//               the branch exists so such a value could never read as a person.
//
// An open credit comes back as kind 'none' with an empty label and renders
// NOTHING at all — it was never closed, so there is no gap to report. Both call
// sites lean on that: they only offer the surface when `closed_by_label` is
// there, so nobody opens a panel to find it empty.
const MUTED_KINDS = new Set(['unknown', 'system']);

export default function CreditClosedBy({ credit }) {
  if (!credit.closed_by_label) return null;

  const muted = MUTED_KINDS.has(credit.closed_by_kind);
  // `.sheet-kv` flexes label and value apart, and the value is always the long
  // side here (an email, a typed note). minWidth 0 lets it shrink below its
  // content, and `anywhere` breaks an address with no space in it — both panels
  // this renders into are narrow, and the point of dropping the table column
  // was to stop truncating the address.
  const value = { textAlign: 'start', minWidth: 0, overflowWrap: 'anywhere' };

  return (
    <>
      <div className="sheet-kv">
        <label>נסגר ע״י</label>
        <span style={{
          ...value,
          color: muted ? 'var(--ink-subtle)' : 'var(--ink)',
          fontStyle: muted ? 'italic' : 'normal',
        }}>{credit.closed_by_label}</span>
      </div>
      {credit.closed_at_label && (
        <div className="sheet-kv">
          <label>תאריך</label>
          <span style={value}>{credit.closed_at_label}</span>
        </div>
      )}
      {/* Only a close with no linked credit document carries a reason — the
          document IS the record in the other case, so there is nothing to ask. */}
      {credit.closed_reason_label && (
        <div className="sheet-kv">
          <label>סיבה</label>
          <span style={value}>{credit.closed_reason_label}</span>
        </div>
      )}
      {credit.closed_note && (
        <div className="sheet-kv">
          <label>הערה</label>
          <span style={{ ...value, color: 'var(--ink-muted)' }}>{credit.closed_note}</span>
        </div>
      )}
    </>
  );
}
